import { find, round } from 'lodash';
import React, { useContext } from 'react';
import { assertNotNull } from '../../../../../../helpers/nullHelpers';
import { TextInputField } from '../../../../../../shared/form/inputs/TextInputField';
import { DesignCalculationReadOnlyField } from '../../../../../designs/design-form/calculations/DesignCalculationReadOnlyField';
import { RoomDesignCalculationsResponse } from '../../../../../designs/design-form/calculations/designCalculationsResponse';
import { floorOutputFormulaTypeEn1264 } from '../../../GetDataForQuoteOrDesignForm';
import { roomFieldName } from '../../../quoteOrDesignFormFieldNameHelpers';
import { UnitFieldContext } from '../../../units/UnitFieldContext';
import { RoomFormModel } from '../../roomFormModel';
import { RoomAutocompleteItem } from '../../roomTypes';
import { RoomFloorConstructionField } from '../RoomFloorConstructionField';
import { RoomNameField } from '../RoomNameField';

// The Other fields for a Room are:
// * Room Name
// * Floor Construction
// * Flow Rate
// * Heat loss [W/m2]
// * Heat Output [W/m2]
// * Heat Loss [W]
// * Heat Output [W]
// * Screed Conductivity
// * Thickness of Screed above Pipe

type Props = {
  unitIndex: number;
  manifoldIndex: number;
  roomIndex: number;
  isWithinRadiatorManifold: boolean;
  room: RoomFormModel;
  handleSelectionOfRoomName: (selectedItem: RoomAutocompleteItem) => void;
  roomDesignCalculationsResponse: RoomDesignCalculationsResponse | null;
};

export const RoomFieldsOther = ({
  unitIndex,
  manifoldIndex,
  roomIndex,
  isWithinRadiatorManifold,
  room,
  handleSelectionOfRoomName,
}: Props) => {
  const unitFieldContext = assertNotNull(useContext(UnitFieldContext));

  const { designCalculations } = unitFieldContext;

  const unitDesignCalculationsResponse = designCalculations && designCalculations.response;
  const designCalculationsInProgress = designCalculations != null && designCalculations.inProgress;
  const designCalculationsError = designCalculations && designCalculations.error;

  const roomDesignCalculationsResult =
    (unitDesignCalculationsResponse &&
      find(
        unitDesignCalculationsResponse.rooms,
        r => r.manifoldIndex === manifoldIndex && r.roomIndex === roomIndex,
      )) ||
    null;

  return (
    <>
      <RoomNameField
        unitIndex={unitIndex}
        manifoldIndex={manifoldIndex}
        roomIndex={roomIndex}
        handleSelectionOfRoomName={handleSelectionOfRoomName}
      />
      <RoomFloorConstructionField
        unitIndex={unitIndex}
        manifoldIndex={manifoldIndex}
        roomIndex={roomIndex}
        isWithinRadiatorManifold={isWithinRadiatorManifold}
      />
      <DesignCalculationReadOnlyField
        label="Flow Rate (L/min)"
        designCalculationsInProgress={designCalculationsInProgress}
        designCalculationsError={designCalculationsError}
        value={roomDesignCalculationsResult?.heatOutput?.flowRateInLitresPerMinute?.toLocaleString() ?? null}
      />
      <DesignCalculationReadOnlyField
        label={
          <>
            Heat Loss (W/m<sup>2</sup>)
          </>
        }
        designCalculationsInProgress={designCalculationsInProgress}
        designCalculationsError={designCalculationsError}
        value={roomDesignCalculationsResult?.heatLoss.heatLossInWattsPerSquareMetre.toLocaleString() ?? null}
      />
      <DesignCalculationReadOnlyField
        label={
          <>
            Heat Output (W/m<sup>2</sup>)
          </>
        }
        designCalculationsInProgress={designCalculationsInProgress}
        designCalculationsError={designCalculationsError}
        value={roomDesignCalculationsResult?.heatOutput?.heatOutputInWattsPerSquareMetre.toLocaleString() ?? null}
      />
      {room.heatLossDetails != null && !room.heatLossDetails.overrideTotalHeatLossInWatts && (
        <>
          <DesignCalculationReadOnlyField
            label="Fabric Loss (W)"
            designCalculationsInProgress={designCalculationsInProgress}
            designCalculationsError={designCalculationsError}
            value={roomDesignCalculationsResult?.heatLoss.fabricHeatLossInWatts?.toLocaleString() ?? null}
          />
          <DesignCalculationReadOnlyField
            label="Ventilation Loss (W)"
            designCalculationsInProgress={designCalculationsInProgress}
            designCalculationsError={designCalculationsError}
            value={roomDesignCalculationsResult?.heatLoss.ventilationHeatLossInWatts?.toLocaleString() ?? null}
          />
        </>
      )}
      {room.heatLossDetails && room.heatLossDetails.overrideTotalHeatLossInWatts ? (
        <TextInputField
          name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'heatLossDetails.userEnteredTotalHeatLossInWatts')}
          label="Total Heat Loss (W)"
        />
      ) : (
        <DesignCalculationReadOnlyField
          label="Total Heat Loss (W)"
          designCalculationsInProgress={designCalculationsInProgress}
          designCalculationsError={designCalculationsError}
          value={
            roomDesignCalculationsResult == null
              ? null
              : round(roomDesignCalculationsResult.heatLoss.totalHeatLossInWatts, 2).toLocaleString() ?? null
          }
        />
      )}
      <DesignCalculationReadOnlyField
        label="Heat Output (W)"
        designCalculationsInProgress={designCalculationsInProgress}
        designCalculationsError={designCalculationsError}
        value={roomDesignCalculationsResult?.heatOutput?.heatOutputInWatts.toLocaleString() ?? null}
      />
      {room.floorConstruction != null &&
        (room.floorConstruction.mainFloorOutputFormulaType === floorOutputFormulaTypeEn1264 ||
          room.floorConstruction.peripheralFloorOutputFormulaType === floorOutputFormulaTypeEn1264) && (
          <>
            <TextInputField
              name={roomFieldName(
                unitIndex,
                manifoldIndex,
                roomIndex,
                'designRoomDetails.screedDetails.screedConductivity',
              )}
              label="Screed Conductivity (W/mK)"
            />
            <TextInputField
              name={roomFieldName(
                unitIndex,
                manifoldIndex,
                roomIndex,
                'designRoomDetails.screedDetails.thicknessOfScreedAbovePipeInMillimetres',
              )}
              label="Thickness of Screed above Pipe (mm)"
            />
          </>
        )}
    </>
  );
};
